// Pulls labels straight from the settings pages so the explain UI never drifts.
// Backend AppliedSettings come in PascalCase (e.g. "FitWorkingDays"); the settings
// pages key their labels in camelCase (e.g. "fitWorkingDays"). We just lower-case
// the first letter to bridge them.

import {
  TIMESHEET_BASIC_LABEL_KEYS,
  TIMESHEET_BASIC_TAB_KEYS,
  TIMESHEET_BASIC_TAB_SCOPE,
} from '@/modules/settings/pages/timesheet-basic/labels'
import {
  TIMESHEET_ADDITIONAL_LABEL_KEYS,
  TIMESHEET_ADDITIONAL_TAB_KEYS,
  TIMESHEET_ADDITIONAL_TAB_SCOPE,
} from '@/modules/settings/pages/timesheet-additional/labels'

export type ExplainSettingsPage = 'timesheet-basic' | 'timesheet-additional' | null

export interface ExplainFieldSpec {
  /** Translation key for the label. */
  labelKey: string
  /** Settings page tab — used to deep-link. */
  page: ExplainSettingsPage
  /** Sub-tab id within the settings page (e.g. 'a' / 'b' / 'c'), if known. */
  tabId: string | null
}

export interface ExplainPageTarget {
  page: 'timesheet-basic' | 'timesheet-additional'
  /** Persistent-tab store scope (where setSelectedTab writes). */
  scope: string
  /** Tab id to select within that scope. */
  tabId: string
}

export const toCamelKey = (pascal: string): string =>
  pascal.length === 0 ? pascal : pascal[0].toLowerCase() + pascal.slice(1)

const toCamel = toCamelKey

// Backend keys whose camelCase form doesn't match a single DTO field on the
// settings page (the page splits them into pairs like Hours+Minutes). Without
// this override they'd resolve to spec.page=null and render as read-only.
const COMPOSITE_KEY_OVERRIDES: Record<
  string,
  { labelKey: string; page: 'timesheet-basic' | 'timesheet-additional'; tabId: string }
> = {
  NightTimeStartHour: {
    labelKey: 'configuration-timesheet.night-work-kp-art151',
    page: 'timesheet-basic',
    tabId: 'b',
  },
  WorkTimeLimitIntervalStartTime: {
    labelKey: 'configuration-timesheet.night-work-uocpk-art21',
    page: 'timesheet-basic',
    tabId: 'b',
  },
  // HolidayStartTime resolves naturally — the DTO has a matching field.
}

export const getFieldSpec = (settingKey: string): ExplainFieldSpec => {
  const override = COMPOSITE_KEY_OVERRIDES[settingKey]
  if (override) {
    return {
      labelKey: override.labelKey,
      page: override.page,
      tabId: override.tabId,
    }
  }

  const camel = toCamel(settingKey)

  const basicLabel = TIMESHEET_BASIC_LABEL_KEYS[camel]
  if (basicLabel) {
    return {
      labelKey: basicLabel,
      page: 'timesheet-basic',
      tabId: TIMESHEET_BASIC_TAB_KEYS[camel] ?? null,
    }
  }

  const additionalLabel = TIMESHEET_ADDITIONAL_LABEL_KEYS[camel]
  if (additionalLabel) {
    return {
      labelKey: additionalLabel,
      page: 'timesheet-additional',
      tabId: TIMESHEET_ADDITIONAL_TAB_KEYS[camel] ?? null,
    }
  }

  return {
    labelKey: `explain.setting.${settingKey}.label`,
    page: null,
    tabId: null,
  }
}

// Picks the page + tab to deep-link to based on majority of rule keys.
export const pickPageTarget = (
  ruleKeys: ReadonlyArray<string>,
): ExplainPageTarget => {
  const pageCount: Record<'timesheet-basic' | 'timesheet-additional', number> = {
    'timesheet-basic': 0,
    'timesheet-additional': 0,
  }
  const tabCount: Record<string, number> = {}

  for (const key of ruleKeys) {
    const spec = getFieldSpec(key)
    if (spec.page === 'timesheet-basic' || spec.page === 'timesheet-additional') {
      pageCount[spec.page] += 1
      if (spec.tabId) {
        const tabKey = `${spec.page}:${spec.tabId}`
        tabCount[tabKey] = (tabCount[tabKey] ?? 0) + 1
      }
    }
  }

  const page =
    pageCount['timesheet-additional'] > pageCount['timesheet-basic']
      ? 'timesheet-additional'
      : 'timesheet-basic'
  const scope =
    page === 'timesheet-basic'
      ? TIMESHEET_BASIC_TAB_SCOPE
      : TIMESHEET_ADDITIONAL_TAB_SCOPE

  // Within the chosen page, pick the tab with the most rule-key hits.
  let tabId = 'a'
  let bestCount = 0
  for (const [key, count] of Object.entries(tabCount)) {
    const [keyPage, keyTab] = key.split(':')
    if (keyPage !== page) continue
    if (count > bestCount) {
      bestCount = count
      tabId = keyTab
    }
  }

  return { page, scope, tabId }
}

// Editability classification for inline modal editing.
// 'editable-bool' — render an enabled FieldsetSwitch; persists as a boolean field.
// 'editable-select' — render an enabled FieldsetSelect with a fixed option set.
// 'editable-input-hours' — render a numeric input clamped to 0-416 (OverhoursLimit).
// 'readonly-composite' — settings page splits this across multiple DTO fields
//                        (e.g. NightTimeStartHour → hours+minutes); not editable here.
// 'readonly-confirmed' — settings page wraps the change in a confirm dialog
//                        (currentSettlementType, firstSettlementPeriodStartDate);
//                        keep that gating, don't expose inline.
// 'readonly-other-page' — field doesn't live on basic/additional pages
//                        (PpkConfiguration, SickLeaveMode, …) — link out instead.
export type ExplainFieldEditability =
  | 'editable-bool'
  | 'editable-select'
  | 'editable-input-hours'
  | 'editable-time-composite'
  | 'readonly-composite'
  | 'readonly-confirmed'
  | 'readonly-other-page'

// Backend AppliedSetting keys (PascalCase) that the settings page renders as
// (hour-select + minute-select) composites. Each maps to one or more fields
// in the basic settings DTO; the hook owns that mapping.
export const TIME_COMPOSITE_KEYS = new Set<string>([
  'HolidayStartTime',
  'NightTimeStartHour',
  'WorkTimeLimitIntervalStartTime',
])

// Backend AppliedSetting keys whose backend type isn't 'enum:*' (typically
// 'timespan') but whose settings-page widget is still a select with a fixed
// option set. Treat them as editable-select.
export const SELECT_OVERRIDE_KEYS = new Set<string>([
  'PlannedHoursRounding',
  'CalculationRounding',
])

// Backend-key (PascalCase) → editability decision, considering the AppliedSetting
// type tag from the backend ('bool', 'enum:X', 'timespan'). Composite/select
// overrides are checked first because their key shape doesn't always match the
// settings DTO field name (which is what `spec.page` is keyed on).
export const getFieldEditability = (
  settingKey: string,
  settingType: string | undefined,
): ExplainFieldEditability => {
  // Composite TimeSpans render as paired hour/minute selects on the settings
  // page; check before page-resolution because two of the three composites
  // don't have a single matching DTO field name.
  if (TIME_COMPOSITE_KEYS.has(settingKey)) {
    return 'editable-time-composite'
  }

  // Backend type 'timespan' but settings page renders as a fixed-option select.
  if (SELECT_OVERRIDE_KEYS.has(settingKey)) {
    return 'editable-select'
  }

  const spec = getFieldSpec(settingKey)
  if (spec.page === null) return 'readonly-other-page'

  // Settings page wraps these in confirm dialogs — preserve that gating.
  if (settingKey === 'CurrentSettlementType' || settingKey === 'FirstSettlementPeriodStartDate') {
    return 'readonly-confirmed'
  }

  // OverhoursLimit shows as "X h" integer; settings DTO stores it as a TimeSpan string.
  if (settingKey === 'OverhoursLimit') return 'editable-input-hours'

  // Backend AppliedSetting key 'ScheduleMode' maps to settings field
  // 'prepareScheduleOnCalculation' (boolean), and 'StartDayFromSchedule' is also
  // boolean-stored-as-select on the page. They're skipped from inline-edit for
  // simplicity (low frequency of inline use).
  if (settingKey === 'ScheduleMode' || settingKey === 'StartDayFromSchedule') {
    return 'readonly-confirmed'
  }

  if (settingType === 'bool') return 'editable-bool'
  if (settingType?.startsWith('enum:')) return 'editable-select'

  // Default: if we don't recognise the type, leave it read-only.
  return 'readonly-composite'
}

